import Head from 'next/head';
import Link from 'next/link';

import Footer from '@/pagecomponents/newcomponents/Footer';
import Navbar from '@/pagecomponents/newcomponents/Navbar';
import { lifeShackProductFacts } from '@src/config/productFacts';
import styles from './About.module.css';

const canonicalUrl = `${lifeShackProductFacts.siteUrl}/about/`;
const reviewedAt = 'September 22, 2026';
const socialImage = `${lifeShackProductFacts.siteUrl}/widget.png`;

export default function About() {
  const founderJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${canonicalUrl}#founder`,
    name: lifeShackProductFacts.founder.name,
    jobTitle: lifeShackProductFacts.founder.title,
    worksFor: {
      '@id': lifeShackProductFacts.organizationId,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${lifeShackProductFacts.siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'About LifeShack',
        item: canonicalUrl,
      },
    ],
  };

  return (
    <>
      <Head>
        <title>About LifeShack</title>
        <meta
          name="description"
          content="Learn how LifeShack helps job seekers reduce repetitive application work, how its application agent works, and who is responsible for the product."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About LifeShack" />
        <meta
          property="og:description"
          content="LifeShack is a cloud-based job application agent built to reduce repetitive application work while keeping candidates in control."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={socialImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About LifeShack" />
        <meta
          name="twitter:description"
          content="LifeShack is a cloud-based job application agent built to reduce repetitive application work while keeping candidates in control."
        />
        <meta name="twitter:image" content={socialImage} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(founderJsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />
      </Head>

      <Navbar />
      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.container}>
            <p className={styles.eyebrow}>
              About LifeShack
            </p>
            <h1 className={styles.heroTitle}>
              Less repetitive application work. More time for the human side
              of a job search.
            </h1>
            <p className={styles.lead}>
              {lifeShackProductFacts.shortDescription}
            </p>
          </div>
        </section>

        <section className={styles.contentSection} aria-labelledby="our-story">
          <div className={styles.container}>
            <h2 id="our-story" className={styles.sectionTitle}>Why LifeShack exists</h2>
            <p className={styles.methodCopy}>
              LifeShack began with a problem founder Patrick Da Costa Guimarais
              saw among people close to him: finding a better job took time they
              did not have. People were staying in roles that made them unhappy
              because they could not give their job search the attention it needed.
            </p>
            <p className={styles.methodCopy}>
              The problem was personal, too. Patrick&apos;s fiancée was struggling
              to find work and later found a job through LifeShack. That experience
              helps explain why he built the product; it is one person&apos;s
              experience, not a promise of the same result for everyone.
            </p>
            <p className={styles.methodCopy}>
              Our purpose is to reduce repetitive application work so a lack of
              time does not stand between someone and their next opportunity.
            </p>
            <h3 className={styles.subheading}>What we focus on</h3>
            <p className={styles.methodCopy}>
              Our priorities are application quality, strong resumes, and relevant
              job opportunities—not simply increasing the number of applications
              sent. We aim to help people present their experience clearly and
              find opportunities that fit their preferences.
            </p>
          </div>
        </section>

        <section className={styles.contentSection}>
          <div className={styles.cards}>
            <article className={styles.card}>
              <h2 className={styles.cardTitle}>What the product does</h2>
              <ul className={styles.factList}>
                {lifeShackProductFacts.capabilities.map((capability) => (
                  <li key={capability}>{capability}</li>
                ))}
              </ul>
            </article>
            <article className={styles.card}>
              <h2 className={styles.cardTitle}>Controls and limitations</h2>
              <p className={styles.methodCopy}>
                You can update your preferences, pause Auto Apply, and block
                companies. You cannot review or approve each application before
                submission. Check your profile and company blocklist before
                enabling Auto Apply.
              </p>
              <ul className={styles.factList}>
                {lifeShackProductFacts.limitations.map((limitation) => (
                  <li key={limitation}>{limitation}</li>
                ))}
              </ul>
            </article>
          </div>
        </section>

        <section className={styles.contentSection} aria-labelledby="products">
          <div className={styles.container}>
            <h2 id="products" className={styles.sectionTitle}>Tools for the application work</h2>
            <div className={styles.productGrid}>
              <article>
                <h3 className={styles.cardTitle}><Link href="/ai-auto-apply/"><a className={styles.methodLink}>AI Auto Apply</a></Link></h3>
                <p className={styles.methodCopy}>Find matching roles and complete supported applications using your saved profile and preferences.</p>
              </article>
              <article>
                <h3 className={styles.cardTitle}><Link href="/ai-resume/"><a className={styles.methodLink}>AI resume tools</a></Link></h3>
                <p className={styles.methodCopy}>Prepare editable resume drafts. Check every draft for accuracy before using it; automated tailoring depends on your plan.</p>
              </article>
              <article>
                <h3 className={styles.cardTitle}><Link href="/ai-cover-letter/"><a className={styles.methodLink}>AI cover letter tools</a></Link></h3>
                <p className={styles.methodCopy}>Prepare editable cover letter drafts. Standalone draft editing is separate from Auto Apply, which submits without per-application approval.</p>
              </article>
            </div>
          </div>
        </section>

        <section className={styles.methodSection} aria-labelledby="workflow">
          <div className={styles.container}>
            <h2 id="workflow" className={styles.sectionTitle}>How LifeShack works</h2>
            <ol className={`${styles.factList} ${styles.steps}`}>
              <li><strong>Set up your profile.</strong> Add your resume, job preferences, and reusable application answers. Keep your information accurate.</li>
              <li><strong>Check your settings.</strong> Review your preferences and company blocklist before enabling Auto Apply.</li>
              <li><strong>Let the agent apply.</strong> LifeShack finds matching roles and submits supported applications automatically. Some employer questions or portal changes can require additional input.</li>
              <li><strong>Track and adjust.</strong> See submitted applications in your dashboard, update preferences, or pause future activity. Pausing does not withdraw applications already submitted.</li>
            </ol>
            <h3 className={styles.subheading}>Who it is designed for</h3>
            <p className={styles.methodCopy}>LifeShack is for job seekers who want to reduce repetitive application work and are comfortable with automatic submission based on their saved information. If you need to approve every application before it is sent, Auto Apply is not the right workflow for that requirement.</p>
          </div>
        </section>

        <section className={styles.contentSection} aria-labelledby="company-facts">
          <div className={styles.container}>
            <h2 id="company-facts" className={styles.sectionTitle}>LifeShack at a glance</h2>
            <dl className={styles.companyFacts}>
              <div><dt>Name</dt><dd>{lifeShackProductFacts.name}</dd></div>
              <div><dt>Headquarters</dt><dd>San Francisco, California</dd></div>
              <div><dt>Product</dt><dd>Cloud-based AI auto-apply software for job seekers</dd></div>
              <div><dt>Founder &amp; CEO</dt><dd><a className={styles.methodLink} href="#founder">{lifeShackProductFacts.founder.name}</a></dd></div>
              <div><dt>Website</dt><dd><a className={styles.methodLink} href={`${lifeShackProductFacts.siteUrl}/`}>lifeshack.com</a></dd></div>
              <div><dt>Getting started</dt><dd>{lifeShackProductFacts.introductoryOffer.description} <Link href="/#plans"><a className={styles.methodLink}>See current plans and limits</a></Link>.</dd></div>
              <div><dt>Support</dt><dd><a className={styles.methodLink} href={`mailto:${lifeShackProductFacts.contact.supportEmail}`}>{lifeShackProductFacts.contact.supportEmail}</a></dd></div>
              <div><dt>Company profiles</dt><dd className={styles.socialLinks}>{lifeShackProductFacts.sameAs.map((url) => <a key={url} className={styles.methodLink} href={url}>{new URL(url).hostname.replace('www.', '')}</a>)}</dd></div>
            </dl>
          </div>
        </section>

        <section className={styles.methodSection}>
          <div className={styles.container}>
            <p className={styles.eyebrow}>
              How we explain and evaluate the product
            </p>
            <h2 className={styles.sectionTitle}>Clear about capabilities and tradeoffs.</h2>
            <p className={styles.methodCopy}>
              Our product and comparison pages distinguish cloud application
              agents from trackers, autofill extensions, browser automation,
              and managed services. Material competitor details are checked
              against official sources and dated so readers can reverify them.
              We describe portal limitations openly and do not promise an
              interview or job offer.
            </p>
            <div className={styles.methodLinks}>
              <Link href="/best-ai-auto-apply-tools/">
                <a className={styles.methodLink}>Read our category methodology</a>
              </Link>
              <Link href="/compare/">
                <a className={styles.methodLink}>Review product comparisons</a>
              </Link>
              <Link href="/privacy/">
                <a className={styles.methodLink}>Read the privacy policy</a>
              </Link>
            </div>
          </div>
        </section>

        <section className={styles.contentSection} aria-labelledby="questions">
          <div className={styles.container}>
            <h2 id="questions" className={styles.sectionTitle}>Questions about LifeShack</h2>
            <h3 className={styles.subheading}>Is LifeShack a recruiter?</h3>
            <p className={styles.methodCopy}>LifeShack provides job application software, not a personal recruiter or a guarantee of employment. Employers decide whom to interview and hire.</p>
            <h3 className={styles.subheading}>Can I approve applications before they are sent?</h3>
            <p className={styles.methodCopy}>No. Auto Apply submits supported applications automatically. You can manage preferences and company blocklists and pause Auto Apply, but there is no per-application approval step.</p>
            <h3 className={styles.subheading}>Does pausing Auto Apply cancel my subscription?</h3>
            <p className={styles.methodCopy}>No. Pausing application activity does not pause or cancel subscription billing. Manage your subscription separately in billing settings.</p>
            <h3 className={styles.subheading}>Can I try LifeShack without a credit card?</h3>
            <p className={styles.methodCopy}>{lifeShackProductFacts.introductoryOffer.description} <Link href="/#plans"><a className={styles.methodLink}>Review paid plans and application limits</a></Link>.</p>
            <h3 className={styles.subheading}>Where can I read about my data?</h3>
            <p className={styles.methodCopy}>Read our <Link href="/privacy/"><a className={styles.methodLink}>privacy policy</a></Link> for information about data handling, or contact the team below with a question.</p>
          </div>
        </section>

        <section className={styles.contactSection}>
          <div className={styles.contactCard}>
            <p className={styles.contactEyebrow}>
              Founder and contact
            </p>
            <h2 id="founder" className={styles.founderName}>
              {lifeShackProductFacts.founder.name}
            </h2>
            <p className={styles.founderTitle}>
              {lifeShackProductFacts.founder.title}
            </p>
            <p className={styles.contactCopy}>
              Patrick&apos;s background is in AI and software engineering,
              building machine-learning systems and software products. He brings
              that experience to LifeShack&apos;s application automation and
              job-search tools.
            </p>
            <p className={styles.contactCopy}>
              Product questions and support requests are handled directly by
              the LifeShack team. Email{' '}
              <a
                className={styles.contactLink}
                href={`mailto:${lifeShackProductFacts.contact.supportEmail}`}
              >
                {lifeShackProductFacts.contact.supportEmail}
              </a>
              .
            </p>
            <p className={styles.reviewedAt}>Page reviewed {reviewedAt}.</p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

About.isPublicSeoPage = true;
About.preloadSeoHero = false;
